import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import EmailVariablePicker from './EmailVariablePicker';
import { Eye, Save } from 'lucide-react';

interface EmailTemplate {
  id: string;
  template_name: string;
  subject: string;
  body_html: string;
  body_text: string;
  variables: string[];
  is_active: boolean;
  category: string;
}

interface EmailTemplateEditorProps {
  template: EmailTemplate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (template: EmailTemplate) => void;
}

export default function EmailTemplateEditor({
  template,
  open,
  onOpenChange,
  onSave,
}: EmailTemplateEditorProps) {
  const [editedTemplate, setEditedTemplate] = useState<EmailTemplate>(template);
  const [activeTab, setActiveTab] = useState<string>('html');

  useEffect(() => {
    setEditedTemplate(template);
  }, [template]);

  const handleInsertVariable = (variable: string, targetField: 'subject' | 'body_html' | 'body_text') => {
    const placeholder = `{{${variable}}}`;
    setEditedTemplate((prev) => ({
      ...prev,
      [targetField]: prev[targetField] + placeholder,
    }));
  };

  const handleSave = () => {
    onSave(editedTemplate);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Email Template</DialogTitle>
          <DialogDescription>
            Customize the email subject and body. Use variables to insert dynamic content.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Switch
                checked={editedTemplate.is_active}
                onCheckedChange={(checked) =>
                  setEditedTemplate((prev) => ({ ...prev, is_active: checked }))
                }
              />
              <Label>Active</Label>
            </div>
            <EmailVariablePicker
              variables={editedTemplate.variables}
              onInsert={(variable) => handleInsertVariable(variable, activeTab as any)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject Line</Label>
            <Input
              id="subject"
              value={editedTemplate.subject}
              onChange={(e) =>
                setEditedTemplate((prev) => ({ ...prev, subject: e.target.value }))
              }
              placeholder="Enter email subject"
            />
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="html">HTML Body</TabsTrigger>
              <TabsTrigger value="text">Plain Text</TabsTrigger>
            </TabsList>

            <TabsContent value="html" className="space-y-2">
              <Label htmlFor="body_html">HTML Body</Label>
              <Textarea
                id="body_html"
                value={editedTemplate.body_html}
                onChange={(e) =>
                  setEditedTemplate((prev) => ({ ...prev, body_html: e.target.value }))
                }
                placeholder="Enter HTML email body"
                className="min-h-[300px] font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                You can use HTML tags for formatting. Variables like {'{{client_name}}'} will be replaced with actual values.
              </p>
            </TabsContent>

            <TabsContent value="text" className="space-y-2">
              <Label htmlFor="body_text">Plain Text Body</Label>
              <Textarea
                id="body_text"
                value={editedTemplate.body_text}
                onChange={(e) =>
                  setEditedTemplate((prev) => ({ ...prev, body_text: e.target.value }))
                }
                placeholder="Enter plain text email body"
                className="min-h-[300px]"
              />
              <p className="text-xs text-muted-foreground">
                Plain text version for email clients that don't support HTML.
              </p>
            </TabsContent>
          </Tabs>

          <div className="rounded-lg border p-4 bg-muted/50">
            <p className="text-sm font-medium mb-2">Available Variables:</p>
            <div className="flex flex-wrap gap-2">
              {editedTemplate.variables.map((variable) => (
                <code key={variable} className="text-xs bg-background px-2 py-1 rounded">
                  {'{{' + variable + '}}'}
                </code>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
